import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Input, Select, Table, Modal } from "antd";
import { fetchMaterialDownloads, retryMaterialDownload } from "../api/adminMaintenanceApi";
import { AssetFile } from "../component/CatalogAssets";
import styles from "./MaterialDownloadsPanel.module.css";

const labels = { PENDING: "В очереди", RUNNING: "Скачивается", STORED: "Сохранено", SKIPPED: "Уже есть файл", UNAVAILABLE: "Не удалось", FAILED: "Ожидает повтора", EXISTING: "Уже есть файл" };
function requestError(error, fallback) {
    try { return JSON.parse(error.message).message || fallback; } catch { return error.message || fallback; }
}
export function materialLink(value) {
    if (!value) return null;
    try {
        const url = new URL(value.startsWith("//") ? `https:${value}` : value);
        return ["http:","https:"].includes(url.protocol) && !url.username && !url.password ? url.href : null;
    } catch { return null; }
}
export function materialError(value) {
    if (!value) return "—";
    if (value.startsWith("DISCOVERY_")) return "Не удалось получить ссылки из карточки: " + value.slice(10);
    if (["NO_LINK", "No source URL"].includes(value)) return "Поставщик не вернул ссылку на файл";
    if (["ReadTimeoutError","ConnectTimeoutError"].includes(value)) return "Превышено время ожидания файла";
    if (value === "BROWSER_UNAVAILABLE") return "Ожидание браузера";
    if (value === "TIMEOUT") return "Превышено время ожидания файла";
    if (value === "DIRECT_UNAVAILABLE") return "Ожидание исполнителя без VPN";
    if (value === "VPN_UNAVAILABLE") return "Ожидание VPN";
    if (value === "INTERRUPTED") return "Попытка прервана";
    if (value === "BROWSER_HTTP_504") return "Браузер не завершил загрузку вовремя";
    if (value === "BROWSER_HTTP_422") return "Браузер не смог скачать файл";
    if (value === "BROWSER_HTTP_400") return "Неподдерживаемая ссылка на файл";
    return value;
}
// [[Material Download Maintenance]] #obsidian-marker
export default function MaterialDownloadsPanel() {
    const [preview, setPreview] = useState(null);
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState({ search: "", status: "", offset: 0, source: "" });
    const [data, setData] = useState({ items: [], hasMore: false });
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState("");
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const sequence = useRef(0);
    const load = useCallback(async () => {
        const id = ++sequence.current;
        setLoading(true);
        try {
            const result = await fetchMaterialDownloads(filter);
            if (id === sequence.current) { setData(result); setError(""); }
        } catch (e) { if (id === sequence.current) setError(requestError(e, "Не удалось загрузить журнал")); }
        finally { if (id === sequence.current) setLoading(false); }
    }, [filter]);
    useEffect(() => {
        load();
        const timer = setInterval(() => { if (!document.hidden) load(); }, 15000);
        return () => { clearInterval(timer); sequence.current += 1; };
    }, [load]);
    async function retry(row) {
        setBusy(`${row.component_id}:${row.kind}`); setMessage(""); setError("");
        try {
            await retryMaterialDownload(row.component_id, row.kind, row.source || "mouser");
            setMessage(`${row.part_number}: ${row.kind === "image" ? "картинка" : "спецификация"} поставлена на повторную загрузку.`);
            await load();
        } catch (e) { setError(requestError(e, "Не удалось повторить загрузку")); }
        finally { setBusy(""); }
    }
    const totals = (data.summary || []).filter(row => !filter.source || row.source === filter.source).reduce((out,row) => { out[row.status]=(out[row.status] || 0)+Number(row.total);return out; }, {});
    const columns = [
        { title: "Источник", dataIndex: "source", width: 120, fixed: "left", render: value => <strong className={`${styles.supplierBadge} ${value === "digikey" ? styles.digikey : styles.mouser}`}>{value === "digikey" ? "DigiKey" : "Mouser"}</strong> },
        { title: "Артикул", dataIndex: "part_number", width: 200 },
        { title: "Производитель", dataIndex: "manufacturer", width: 220 },
        { title: "Файл", dataIndex: "kind", width: 140, render: kind => kind === "image" ? "Картинка" : "Спецификация PDF" },
        { title: "Просмотр", width: 200, render: (_,row) => <div>
            {row.stored && <Button type="link" onClick={() => setPreview(row)}>Открыть из MinIO</Button>}
            {materialLink(row.source_url) ? <a href={materialLink(row.source_url)} target="_blank" rel="noopener noreferrer">Ссылка поставщика ↗</a> : !row.stored ? "Нет ссылки" : null}
        </div> },
        { title: "Статус", dataIndex: "status", width: 140, render: value => labels[value] || value },
        { title: "Причина", dataIndex: "error", width: 260, render: materialError },
        { title: "Следующая попытка", dataIndex: "next_attempt", width: 180, render: (value,row) => row.status === "FAILED" && value ? new Date(value).toLocaleString("ru-RU") : "—" },
        { title: "Обновлено", dataIndex: "updated_at", width: 180, render: value => value ? new Date(value).toLocaleString("ru-RU") : "—" },
        { title: "Действие", width: 170, render: (_, row) => row.can_retry ?
            <Button disabled={!!busy} loading={busy === `${row.component_id}:${row.kind}`} onClick={() => retry(row)}>Повторить загрузку</Button> : "—" }
    ];
    return <section className={styles.panel} aria-label="Загрузка материалов DigiKey и Mouser">
        <h2>Сбор данных</h2>
        <p>Загружаются в фоне, независимо от цен и остатков. Журнал обновляется каждые 15 секунд.
            Повтор сбрасывает только неудачную попытку выбранного файла. Сохранённые файлы не удаляются.</p>
        <p>Если ссылки нет, сначала нужен новый ответ поставщика со ссылкой. При повторе проверяется свежий кеш API.</p>
        <div className={styles.suppliers} role="group" aria-label="Поставщик материалов">
            {[["", "Все поставщики"], ["digikey", "DigiKey"], ["mouser", "Mouser"]].map(([value,label]) =>
                <Button key={value} type={filter.source === value ? "primary" : "default"}
                    aria-pressed={filter.source === value} onClick={() => { setFilter(f => ({...f,source:value,offset:0})); setMessage(""); }}>{label}</Button>)}
        </div>
        <h3>{filter.source === "digikey" ? "Загрузки DigiKey" : filter.source === "mouser" ? "Загрузки Mouser" : "Загрузки всех поставщиков"}</h3>
        <div className={styles.summary} aria-label="Общая очередь файлов">
            <span>Всего в работе: <b>{(totals.PENDING || 0)+(totals.RUNNING || 0)+(totals.FAILED || 0)}</b></span>
            <span>В очереди / обработке: <b>{totals.PENDING || 0}</b></span>
            <span>Скачивается (Mouser): <b>{totals.RUNNING || 0}</b></span>
            <span>Ожидает повтора: <b>{totals.FAILED || 0}</b></span>
            <span>Сохранено: <b>{totals.STORED || 0}</b></span>
            <span>Не удалось: <b>{totals.UNAVAILABLE || 0}</b></span>
        </div>
        <p>Счётчики выбранного поставщика по всей базе, независимо от поиска и фильтра статуса. DigiKey обрабатывает файлы последовательно;
            текущий скачиваемый файл входит в «В очереди / обработке». Ошибки со статусом «Ожидает повтора» повторяются автоматически.</p>
        <div className={styles.toolbar}>
            <Input.Search aria-label="Артикул" placeholder="Поиск по артикулу" value={search} onChange={e => setSearch(e.target.value)}
                onSearch={() => setFilter(f => ({ ...f, search, offset: 0 }))} enterButton="Найти" />
            <Select aria-label="Статус загрузки" value={filter.status} options={[{ value: "", label: "Все статусы" }, ...Object.entries(labels).map(([value, label]) => ({ value, label }))]}
                onChange={status => setFilter(f => ({ ...f, status, offset: 0 }))} />
            <Button onClick={load} loading={loading}>Обновить</Button>
        </div>
        {error && <Alert type="error" showIcon message={error} />}
        {message && <Alert type="success" showIcon message={message} />}
        <Table rowKey={r => `${r.source}:${r.component_id}:${r.kind}`} columns={columns} dataSource={data.items} loading={loading}
            pagination={false} scroll={{ x: 1310 }} size="small" locale={{ emptyText: "Заданий по выбранным условиям нет" }} />
        <div className={styles.toolbar}>
            <Button disabled={loading || filter.offset === 0} onClick={() => setFilter(f => ({ ...f, offset: Math.max(0, f.offset - 50) }))}>Назад</Button>
            <span>Страница {filter.offset / 50 + 1}</span>
            <Button disabled={loading || !data.hasMore} onClick={() => setFilter(f => ({ ...f, offset: f.offset + 50 }))}>Далее</Button>
        </div>
        <Modal open={!!preview} onCancel={() => setPreview(null)} footer={null} centered width={960} destroyOnHidden
            title={preview ? `${preview.part_number} — ${preview.kind === "image" ? "картинка" : "спецификация"}` : "Просмотр файла"}>
            {preview && <AssetFile key={`${preview.component_id}:${preview.kind}`} partNumber={preview.part_number}
                asset={{stored:true,kind:preview.kind,url:`/api/components/assets/file?${new URLSearchParams({componentId:preview.component_id,kind:preview.kind})}`}} />}
        </Modal>
    </section>;
}
